import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Droplets, Plus, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Note: the backing collection is still `water_sources` for historical
// reasons. Surface name is "Amenities & Facilities" everywhere user-facing.
export const WATER_TYPES = [
  // Water
  { value: "stream_river", label: "Stream / River", emoji: "🏞️", group: "Water" },
  { value: "lake_pond", label: "Lake / Pond", emoji: "🦆", group: "Water" },
  { value: "public_tap", label: "Public Tap", emoji: "🚰", group: "Water" },
  { value: "trough", label: "Trough", emoji: "🪣", group: "Water" },
  // Refreshments
  { value: "pub", label: "Pub", emoji: "🍺", group: "Refreshments" },
  { value: "cafe", label: "Cafe", emoji: "☕", group: "Refreshments" },
  { value: "camp_shop", label: "Camp Shop", emoji: "🛒", group: "Refreshments" },
  // Facilities
  { value: "toilets_showers", label: "Toilets / Showers", emoji: "🚻", group: "Facilities" },
  { value: "waste_bin", label: "Waste Bin", emoji: "🗑️", group: "Facilities" },
  { value: "dog_wash", label: "Dog Wash", emoji: "🚿", group: "Facilities" },
  { value: "charging_point", label: "Charging Point", emoji: "⚡", group: "Facilities" },
  { value: "parking", label: "Parking", emoji: "🅿️", group: "Facilities" },
  { value: "other", label: "Other", emoji: "📍", group: "Other" },
];

export const DOG_RULES = [
  { value: "inside_out", label: "Dogs inside & out" },
  { value: "outside_only", label: "Outside seating only" },
  { value: "on_lead_only", label: "On-lead only" },
  { value: "secure_area", label: "Secure / fenced area" },
  { value: "unknown", label: "Unknown" },
];

const VENUE_NAME_TYPES = new Set(["pub", "cafe", "camp_shop", "other"]);
const DOG_RULES_TYPES = new Set(["pub", "cafe", "toilets_showers", "camp_shop"]);

export function shouldShowVenueName(t) { return VENUE_NAME_TYPES.has(t); }
export function shouldShowDogRules(t) { return DOG_RULES_TYPES.has(t); }

function fmt(value) {
  return WATER_TYPES.find((w) => w.value === value) || { label: value, emoji: "📍" };
}

function dogRuleLabel(v) {
  return DOG_RULES.find((d) => d.value === v)?.label || "";
}

function AddWaterSourceModal({ locationId, onClose }) {
  const qc = useQueryClient();
  const [waterType, setWaterType] = useState("");
  const [venueName, setVenueName] = useState("");
  const [dogRules, setDogRules] = useState("");
  const [description, setDescription] = useState("");

  const showVenue = shouldShowVenueName(waterType);
  const showDogRules = shouldShowDogRules(waterType);

  const mut = useMutation({
    mutationFn: async () => {
      if (!waterType) throw new Error("Please pick an amenity type");
      const payload = {
        water_type: waterType,
        description: description.trim() || undefined,
      };
      if (showVenue && venueName.trim()) payload.venue_name = venueName.trim();
      if (showDogRules && dogRules) payload.dog_rules = dogRules;
      return (await api.post(`/locations/${locationId}/water-sources`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Amenity added — thanks for sharing!");
      qc.invalidateQueries({ queryKey: ["water-sources", locationId] });
      onClose?.();
    },
    onError: (e) => toast.error(e?.message || e?.response?.data?.detail || "Couldn't add amenity"),
  });

  return (
    <div
      className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="add-water-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 animate-fade-up max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
            <Droplets size={18} className="text-brand-primary" /> Add an amenity
          </h3>
          <button onClick={onClose} className="text-brand-subtle" aria-label="Close" data-testid="add-water-close">
            <X size={20} />
          </button>
        </div>
        <p className="text-xs text-brand-subtle mb-4">Help fellow walkers find what's nearby.</p>

        <label className="block text-sm font-medium text-brand-text">
          Type <span className="text-brand-error">*</span>
        </label>
        <select
          data-testid="add-water-type"
          value={waterType}
          onChange={(e) => setWaterType(e.target.value)}
          className="td-input mt-1"
        >
          <option value="">— Choose a type —</option>
          {["Water", "Refreshments", "Facilities", "Other"].map((g) => (
            <optgroup key={g} label={g}>
              {WATER_TYPES.filter((t) => t.group === g).map((t) => (
                <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
              ))}
            </optgroup>
          ))}
        </select>

        {showVenue && (
          <div data-testid="add-water-venue-wrapper" className="animate-fade-up">
            <label className="block text-sm font-medium text-brand-text mt-4">Venue name</label>
            <input
              data-testid="add-water-venue-name"
              value={venueName}
              onChange={(e) => setVenueName(e.target.value)}
              maxLength={120}
              placeholder="e.g. The Plough Inn"
              className="td-input mt-1"
            />
          </div>
        )}

        {showDogRules && (
          <div data-testid="add-water-dog-rules-wrapper" className="animate-fade-up">
            <label className="block text-sm font-medium text-brand-text mt-4">Dog policy</label>
            <select
              data-testid="add-water-dog-rules"
              value={dogRules}
              onChange={(e) => setDogRules(e.target.value)}
              className="td-input mt-1"
            >
              <option value="">— If you know —</option>
              {DOG_RULES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        )}

        <label className="block text-sm font-medium text-brand-text mt-4">Description (optional)</label>
        <textarea
          data-testid="add-water-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={400}
          placeholder="How do you find it? (e.g., 'Bin behind the gate' or 'Shop by reception')"
          className="td-input mt-1 resize-none"
        />

        <div className="flex gap-2 mt-5">
          <button data-testid="add-water-cancel" onClick={onClose} className="td-btn-secondary flex-1 !py-2.5">Cancel</button>
          <button
            data-testid="add-water-submit"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !waterType}
            className="td-btn-primary flex-1 !py-2.5"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add amenity
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WaterSourcesSection({ locationId }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const q = useQuery({
    queryKey: ["water-sources", locationId],
    queryFn: async () => (await api.get(`/locations/${locationId}/water-sources`)).data,
  });

  const flagDryMut = useMutation({
    mutationFn: (wid) => api.post(`/water-sources/${wid}/flag-dry`, {}),
    onSuccess: () => {
      toast.success("Flagged — thanks, we'll spread the word");
      qc.invalidateQueries({ queryKey: ["water-sources", locationId] });
    },
    onError: () => toast.error("Couldn't flag — try again"),
  });

  const items = q.data?.items || [];
  const available = useMemo(() => items.filter((w) => w.is_available), [items]);
  const dry = useMemo(() => items.filter((w) => !w.is_available), [items]);

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="water-sources-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="td-section-label flex items-center gap-1.5">
            <Droplets size={13} className="text-brand-primary" /> Amenities &amp; Facilities
          </div>
          <p className="text-sm text-brand-subtle mt-1">
            Community-mapped taps, pubs, bins &amp; more. Tap "Not available" if something has gone.
          </p>
        </div>
        <button
          data-testid="add-water-btn"
          onClick={() => setAdding(true)}
          className="td-btn-secondary !py-2 !px-3 !text-xs whitespace-nowrap"
        >
          <Plus size={13} /> Add
        </button>
      </div>

      {q.isLoading ? (
        <div className="py-6 text-center text-brand-subtle text-sm"><Loader2 size={14} className="animate-spin inline" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-brand-subtle text-sm" data-testid="water-sources-empty">
          No amenities reported yet — be the first to share one.
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-brand-border/60" data-testid="water-sources-list">
          {available.map((w) => {
            const t = fmt(w.water_type);
            const headline = w.venue_name || t.label;
            return (
              <li key={w.water_source_id} className="py-3 flex items-start gap-3" data-testid={`water-row-${w.water_source_id}`}>
                <div className="text-2xl shrink-0" aria-hidden>{t.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-text truncate">{headline}</div>
                  {w.venue_name && (
                    <div className="text-[11px] uppercase tracking-wider text-brand-subtle">{t.label}</div>
                  )}
                  {w.dog_rules && (
                    <div className="text-xs text-brand-primary mt-0.5">🐾 {dogRuleLabel(w.dog_rules)}</div>
                  )}
                  {w.description && <div className="text-sm text-brand-subtle mt-0.5">{w.description}</div>}
                  <div className="text-[11px] text-brand-subtle mt-1">Added by {w.submitted_by_name}</div>
                </div>
                <button
                  data-testid={`flag-dry-${w.water_source_id}`}
                  onClick={() => flagDryMut.mutate(w.water_source_id)}
                  disabled={flagDryMut.isPending}
                  className="td-btn-secondary !py-1.5 !px-3 !text-xs !text-brand-accent !border-brand-accent/40 whitespace-nowrap"
                >
                  <AlertTriangle size={11} /> Not available
                </button>
              </li>
            );
          })}

          {dry.length > 0 && (
            <>
              <li className="pt-4 pb-1 td-section-label text-brand-subtle">Recently unavailable · {dry.length}</li>
              {dry.map((w) => {
                const t = fmt(w.water_type);
                const headline = w.venue_name || t.label;
                return (
                  <li key={w.water_source_id} className="py-2.5 flex items-center gap-3 opacity-60" data-testid={`water-dry-${w.water_source_id}`}>
                    <div className="text-xl" aria-hidden>{t.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-brand-text line-through decoration-brand-error/60 truncate">{headline}</div>
                      {w.description && <div className="text-xs text-brand-subtle">{w.description}</div>}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-brand-error font-semibold">gone</span>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      )}

      {adding && <AddWaterSourceModal locationId={locationId} onClose={() => setAdding(false)} />}
    </section>
  );
}
